import React from 'react'
import { stringify } from 'query-string'
import clsx from 'clsx'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text'
import { Badge } from '@wartek-id/badge'
import type { ButtonProps } from '@wartek-id/button'
import { Button } from '@wartek-id/button'
import { Icon } from '@wartek-id/icon'
import { useToast } from '@wartek-id/toast'

import { AksiNyataSessionState, AppealState } from 'types/microlearning'
import { MILKvKeys, useGetKeyPairValue } from 'api/microlearning/useAPIKeyValue'
import RevisionSheet from './CertificateSheet/RevisionSheet'
import AppealSheet from './CertificateSheet/AppealSheet'
import { getCardDataValues } from './CertificateStatusCard.helpers'
import { useScreenSizeContext } from 'utils/hooks/context/useScreenSizeContext'

import type { TAPIGetAksiNyataRevisionResponse } from 'types/microlearning'

enum StatusCardState {
  RevisionCard = 'REVISION',
  AppealCard = 'APPEAL',
  Closed = '',
}
interface CertificateStatusCardProps {
  state: AksiNyataSessionState
  topicName: string
  revisionItems?: string[]
  certifiable?: boolean
  appeal: TAPIGetAksiNyataRevisionResponse['appeal']
  refetchProgress: Function
}

const CertificateStatusCard = (props: CertificateStatusCardProps) => {
  const toast = useToast()
  const { isDesktop } = useScreenSizeContext()
  const [isOpen, setIsOpen] = React.useState<StatusCardState>(
    StatusCardState.Closed
  )
  const router = useRouter()
  const topikId = router.query.topik as string
  const { data: faqSertifikatMandiriKvData } = useGetKeyPairValue(
    MILKvKeys.FAQSertifikatMandiri
  )
  const faqSertifikatMandiriLink = faqSertifikatMandiriKvData?.data?.link || '#'

  const onAppealSubmitted = async () => {
    toast({
      message: `Pengajuan banding telah dikirim!`,
    })
    await props.refetchProgress()
    setIsOpen(StatusCardState.Closed)
  }

  const handleMainCardButtonClick = () => {
    if (props.state === AksiNyataSessionState.Validated) {
      router.push(
        `/${
          process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
          'pelatihan-mandiri'
        }/topik/${topikId}/sertifikat`
      )
      return
    }

    if (props.state === AksiNyataSessionState.NeedRevision) {
      setIsOpen(StatusCardState.RevisionCard)
      return
    }

    router.push(faqSertifikatMandiriLink)
  }

  const revisionUrl = `/${
    process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH || 'pelatihan-mandiri'
  }/topik/${topikId}/aksi-nyata/form?${stringify({
    topik_name: props.topicName,
  })}`

  const cardData = getCardDataValues(
    props.state,
    props.appeal?.state,
    props.certifiable
  )

  const revisionMessage =
    props.appeal?.state === AppealState.Declined
      ? props.appeal?.review
      : props.revisionItems[0] ?? ''

  const isAppealEligible =
    props.appeal.quota > 0 &&
    [AppealState.NotCreated, AppealState.Declined].includes(props.appeal.state)

  return (
    <>
      <div className="mx-3 my-4 mt-1">
        <Text variant="heading-md">Sertifikat</Text>
        <div className={isDesktop ? 'grid grid-cols-2 gap-2' : null}>
          <div className="flex gap-3 border border-subdued rounded p-4 bg-white mt-2">
            <div
              className={clsx(
                'flex justify-center flex-shrink-0 items-center w-8 h-8 rounded-full overflow-hidden',
                cardData.iconBackground
              )}
            >
              <Icon as="i" fontSize="small" color="inverse">
                download_outlined
              </Icon>
            </div>
            <div className="w-full">
              <Badge className="mb-2">{cardData.badgeText}</Badge>
              <Text
                color="subdued"
                variant="body-sm"
                data-testid="card-element"
              >
                <cardData.cardElement />
              </Text>
              {cardData.buttonText && (
                <Button
                  data-testid="main-button"
                  color={cardData.buttonVariant as ButtonProps['color']}
                  fullWidth
                  size="md"
                  className="mt-4"
                  onClick={handleMainCardButtonClick}
                >
                  {cardData.buttonText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      {props.revisionItems?.length > 0 && (
        <>
          <RevisionSheet
            isOpen={isOpen === StatusCardState.RevisionCard}
            onClickAppeal={() => setIsOpen(StatusCardState.AppealCard)}
            onClickFix={() => router.push(revisionUrl)}
            revisionMessage={revisionMessage}
            setIsOpen={setIsOpen}
            isAppealEligible={isAppealEligible}
          />
          <AppealSheet
            isOpen={isOpen === StatusCardState.AppealCard}
            setIsOpen={setIsOpen}
            appeal={props.appeal}
            onAppealSubmitted={onAppealSubmitted}
          />
        </>
      )}
    </>
  )
}

export default CertificateStatusCard
